import React, { useState, useRef, useEffect, useCallback } from 'react';
import { CampingSite } from '@/services/campingSite/types';
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from 'react-router-dom';
import MapInitializer from './components/MapInitializer';
import MapErrorHandler from './components/MapErrorHandler';
import SiteInfoWindow from './components/SiteInfoWindow';
import SitePopup from './SitePopup';
import AvailabilityLegend from './components/AvailabilityLegend';
import { Maximize, Minimize, HelpCircle, Calendar } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

interface MapDisplayProps {
  sites: CampingSite[];
  mapsLoaded: boolean;
  locationFilter: string;
  onSiteSelect: (siteId: string) => void;
  selectedSiteId: string | null;
  startDate?: Date;
  endDate?: Date;
  usePopupCard?: boolean;
  siteType: 'rv' | 'wild-horse';
}

const MapDisplay: React.FC<MapDisplayProps> = ({
  sites,
  mapsLoaded,
  locationFilter,
  onSiteSelect,
  selectedSiteId,
  startDate,
  endDate,
  usePopupCard = false,
  siteType
}) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const mapRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const googleMapRef = useRef<google.maps.Map | null>(null);
  const markers = useRef<(google.maps.Marker | google.maps.marker.AdvancedMarkerElement)[]>([]);
  const infoWindow = useRef<google.maps.InfoWindow | null>(null);
  const [mapInitialized, setMapInitialized] = useState(false);
  const [mapError, setMapError] = useState<string | null>(null);
  const [domainError, setDomainError] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showLegend, setShowLegend] = useState(false);

  const selectedSite = selectedSiteId ? sites.find(site => site.id === selectedSiteId) : null;
  const datesSelected = !!startDate && !!endDate;

  useEffect(() => {
    setShowLegend(!!startDate && !!endDate);
  }, [startDate, endDate]);

  const handleClosePopup = useCallback(() => {
    onSiteSelect('');
  }, [onSiteSelect]);

  const handleBookNow = useCallback((site: CampingSite) => {
    if (!site) return;
    
    const siteType = site.siteType === 'rv' ? 'rv' : 'camping';
    
    console.log(`Initiating booking process for site ${site.id}`);
    
    toast({
      title: "Booking initiated",
      description: `You're booking ${site.siteNumber}. You'll select dates in the next step.`
    });
    
    navigate(`/booking/${siteType}/${site.id}`);
  }, [navigate, toast]);

  const toggleFullscreen = useCallback(() => {
    if (!isFullscreen) {
      if (containerRef.current?.requestFullscreen) {
        containerRef.current.requestFullscreen().catch(err => {
          console.error(`Error attempting to enable fullscreen mode: ${err.message}`);
          toast({
            title: "Fullscreen Error",
            description: `Could not enter fullscreen mode: ${err.message}`,
            variant: "destructive"
          });
        });
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen().catch(err => {
          console.error(`Error attempting to exit fullscreen mode: ${err.message}`);
        });
      }
    }
  }, [isFullscreen, toast]);

  const toggleLegend = useCallback(() => {
    setShowLegend(prev => !prev);
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
      if (googleMapRef.current && window.google?.maps) {
        setTimeout(() => {
          window.google.maps.event.trigger(googleMapRef.current, 'resize');
        }, 200);
      }
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  useEffect(() => {
    if (googleMapRef.current && mapInitialized && selectedSiteId) {
      const selectedSiteObj = sites.find(site => site.id === selectedSiteId);
      
      if (selectedSiteObj?.latitude && selectedSiteObj?.longitude) {
        const currentZoom = googleMapRef.current.getZoom();
        
        googleMapRef.current.panTo({
          lat: selectedSiteObj.latitude,
          lng: selectedSiteObj.longitude
        });
        
        if (currentZoom) {
          googleMapRef.current.setZoom(currentZoom);
        }
      }
    }
  }, [selectedSiteId, sites, mapInitialized]);

  useEffect(() => {
    const handleGoogleMapsError = (event: ErrorEvent) => {
      if (event.filename?.includes('maps.googleapis.com')) {
        console.error('Google Maps API error detected in MapDisplay:', event.message);
        if (event.message?.includes('RefererNotAllowedMapError')) {
          setDomainError(true);
          const currentDomain = window.location.hostname;
          setMapError(`Your Google Maps API key is not authorized for domain: ${currentDomain}. Please add this domain to your Google Cloud Console restrictions.`);
        }
      }
    };
    window.addEventListener('error', handleGoogleMapsError);
    return () => {
      window.removeEventListener('error', handleGoogleMapsError);
    };
  }, []);

  return (
    <div>
      <MapErrorHandler error={mapError} domainError={domainError} />
      
      <div className="relative w-full h-[600px] rounded-lg overflow-hidden border-2 border-tribal-primary/10 shadow-md">
        {datesSelected && (
          <div className="absolute top-4 left-4 z-30 animate-in fade-in slide-in-from-left-2">
            <Badge variant="outline" className="bg-white border border-tribal-primary/20 shadow-sm flex items-center gap-1 py-2 px-3">
              <Calendar className="h-4 w-4 text-tribal-primary" />
              <span>Showing availability for selected dates</span>
            </Badge>
          </div>
        )}
        
        <div 
          ref={containerRef} 
          className={`relative w-full h-full ${isFullscreen ? 'fullscreen' : ''}`}
        >
          <div 
            ref={mapRef} 
            className="w-full h-full"
          />
          
          <div className="absolute top-4 right-4 z-30 flex flex-col gap-2">
            <Button
              onClick={toggleFullscreen}
              size="icon"
              variant="outline"
              className="bg-white hover:bg-gray-100 transition-colors border border-gray-200 h-10 w-10 rounded-full shadow-md"
              aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
              title={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
            >
              {isFullscreen ? (
                <Minimize className="h-5 w-5 text-gray-700" />
              ) : (
                <Maximize className="h-5 w-5 text-gray-700" />
              )}
            </Button>
            
            {startDate && endDate && (
              <Button
                onClick={toggleLegend}
                size="icon"
                variant="outline" 
                className="bg-white hover:bg-gray-100 transition-colors border border-gray-200 h-10 w-10 rounded-full shadow-md"
                aria-label="Toggle legend"
                title="Toggle availability legend"
              >
                <HelpCircle className="h-5 w-5 text-gray-700" />
              </Button>
            )}
          </div>

          {usePopupCard && selectedSite && (
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 w-[90%] max-w-md z-[999] animate-in fade-in-0 zoom-in-95">
              <SitePopup
                site={selectedSite}
                onClose={handleClosePopup}
                startDate={startDate}
                endDate={endDate}
                isFullscreen={isFullscreen}
                containedInMap={true}
                handleBookNow={handleBookNow}
              />
            </div>
          )}
          
          {/* Availability Legend */}
          <AvailabilityLegend isVisible={showLegend && !!startDate && !!endDate} />
        </div>
        
        <MapInitializer 
          mapRef={mapRef}
          googleMapRef={googleMapRef}
          sites={sites}
          locationFilter={locationFilter}
          mapInitialized={mapInitialized}
          setMapInitialized={setMapInitialized}
          setMapError={setMapError}
          markers={markers}
          onSiteSelect={onSiteSelect}
          mapsLoaded={mapsLoaded}
          siteType={siteType}
          startDate={startDate}
          endDate={endDate}
        />
      </div>
      
      {!usePopupCard && (
        <SiteInfoWindow
          selectedSite={selectedSite}
          mapInitialized={mapInitialized}
          googleMapRef={googleMapRef}
          infoWindow={infoWindow}
          onSiteSelect={onSiteSelect}
          handleBookNow={handleBookNow}
        />
      )}

      <style>
        {`
        .fullscreen {
          position: fixed !important;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 9999;
          width: 100vw !important;
          height: 100vh !important;
          margin: 0 !important;
          padding: 0 !important;
          border-radius: 0 !important;
        }
        
        .z-[999] {
          z-index: 999;
        }
        
        .z-[9999] {
          z-index: 9999;
        }
        
        .z-30 {
          z-index: 30;
        }
        
        .z-50 {
          z-index: 50;
        }
        
        .z-60 {
          z-index: 60;
        }
        `}
      </style>
    </div>
  );
};

export default MapDisplay;
